/**
 * Stammdaten: die Ausstattungsliste einlesen (ADR 0003, ADR 0030).
 *
 * Die Datei aus THWin ist die Wahrheit -- auch für Geräte, die in der App
 * angelegt oder geändert wurden. Vor dem Übernehmen steht da, was geschieht:
 * neue Geräte, geänderte mit „vorher → nachher“, und was nicht mehr in der
 * Datei steht. Letzteres lässt sich einzeln archivieren, muss aber nicht.
 *
 * Eingelesen werden nur Geräte. Ort und Status ändert der Import nie.
 *
 * Nur für Administratoren und nur mit Netz (ADR 0006).
 */

import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import type {
  ImportErgebnis,
  ImportFeld,
  ImportLauf,
  ImportVorschau,
  SpaltenZuordnung,
} from "@thw/contracts";
import { anfrage, ServerFehler, sitzungLesen } from "../../api/klient.js";
import { useOnline } from "../../api/online.js";
import { abgleichen } from "../../lokal/abgleich.js";
import { Karte, Knopf, Leer, Nummer, NurMitNetz, Rueckmeldung } from "../../ui/bausteine.jsx";

const FELD_TEXT: Record<ImportFeld | "pruefungen", string> = {
  inventarnummer: "Inventarnummer",
  bezeichnung: "Bezeichnung",
  einheit_zustaendig: "Zuständige Einheit",
  baujahr: "Baujahr",
  hersteller: "Hersteller",
  sachnummer: "Sachnummer",
  geraetenummer: "Gerätenummer",
  kennzeichen: "Kennzeichen",
  pruefart: "Prüfart",
  letzte_pruefung: "Letzte Prüfung",
  pruef_ergebnis: "Prüfergebnis",
  faellig: "Fällig",
  ignorieren: "— nicht übernehmen",
  pruefungen: "Prüfungen",
};

const FELDER = Object.keys(FELD_TEXT).filter((f) => f !== "pruefungen") as ImportFeld[];

function feldName(feld: string): string {
  return (FELD_TEXT as Record<string, string | undefined>)[feld] ?? feld;
}

/** Die Datei geht als reiner Bytestrom -- die Kodierung erkennt der Server. */
async function hochladen(datei: File): Promise<ImportVorschau> {
  const sitzung = sitzungLesen();
  const antwort = await fetch(`/api/core/import?dateiname=${encodeURIComponent(datei.name)}`, {
    method: "POST",
    headers: {
      "content-type": "application/octet-stream",
      ...(sitzung === null ? {} : { authorization: `Bearer ${sitzung.token}` }),
    },
    body: datei,
  });
  const daten: unknown = await antwort.json();
  if (!antwort.ok) {
    const fehler = daten as { fehler?: string };
    throw new ServerFehler(fehler.fehler ?? `Fehler ${String(antwort.status)}`, antwort.status, []);
  }
  return daten as ImportVorschau;
}

function datum(iso: string): string {
  return new Date(iso).toLocaleString("de-DE", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

export function ImportSeite() {
  const online = useOnline();
  const zwischenspeicher = useQueryClient();
  const [vorschau, setVorschau] = useState<ImportVorschau | null>(null);
  const [zuordnung, setZuordnung] = useState<SpaltenZuordnung>([]);
  const [archivieren, setArchivieren] = useState<ReadonlySet<string>>(new Set());
  const [ergebnis, setErgebnis] = useState<ImportErgebnis | null>(null);
  const [fehler, setFehler] = useState<string | null>(null);
  const [laeuft, setLaeuft] = useState(false);

  const laeufe = useQuery({
    queryKey: ["import-laeufe"],
    queryFn: () => anfrage<{ laeufe: ImportLauf[] }>("GET", "/api/core/import"),
    enabled: online,
  });

  if (sitzungLesen()?.benutzer.rolle !== "administrator") {
    return <Rueckmeldung art="warnung" kopf="Nur für Administratoren" />;
  }

  const ausfuehren = async (tun: () => Promise<void>): Promise<void> => {
    setLaeuft(true);
    setFehler(null);
    try {
      await tun();
    } catch (f) {
      setFehler(f instanceof Error ? f.message : String(f));
    } finally {
      setLaeuft(false);
    }
  };

  const neueVorschau = (v: ImportVorschau): void => {
    setVorschau(v);
    setZuordnung(v.zuordnung);
    setArchivieren(new Set());
    setErgebnis(null);
  };

  const zuordnungGeaendert =
    vorschau !== null && zuordnung.some((z, i) => z !== vorschau.zuordnung[i]);

  return (
    <div className="flex flex-col gap-4">
      <h1 className="text-2xl font-bold">Ausstattungsliste einlesen</h1>
      <p className="text-neutral-600 dark:text-neutral-300">
        Die Datei aus THWin ist die Wahrheit — auch für Geräte, die in der App angelegt wurden.
        Eingelesen werden nur Geräte; Ort und Status ändert der Import nie.
      </p>
      {!online && <NurMitNetz />}
      {fehler !== null && <Rueckmeldung art="warnung" kopf="Nicht eingelesen" zusatz={fehler} />}
      {ergebnis !== null && (
        <Rueckmeldung
          art="gut"
          kopf="✓ Übernommen"
          zusatz={`${String(ergebnis.angelegt)} neu, ${String(ergebnis.aktualisiert)} geändert, ${String(ergebnis.unveraendert)} unverändert${
            (ergebnis.archiviert ?? 0) > 0 ? `, ${String(ergebnis.archiviert)} archiviert` : ""
          }.`}
        />
      )}

      <Karte titel="1. Datei wählen">
        <label className="flex min-h-14 cursor-pointer items-center justify-center rounded-xl bg-blue-700 px-4 font-semibold text-white active:bg-blue-800">
          {laeuft ? "Wird gelesen …" : "CSV-Datei auswählen"}
          <input
            type="file"
            accept=".csv,text/csv,application/vnd.ms-excel"
            className="sr-only"
            aria-label="CSV-Datei auswählen"
            disabled={!online || laeuft}
            onChange={(e) => {
              const datei = e.target.files?.[0];
              e.target.value = "";
              if (datei === undefined) return;
              void ausfuehren(async () => {
                neueVorschau(await hochladen(datei));
              });
            }}
          />
        </label>
        <p className="text-sm text-neutral-600 dark:text-neutral-300">
          Die Ausstattungs- oder Prüfliste als CSV. Übernommen wird erst nach der Übersicht.
        </p>
      </Karte>

      {vorschau !== null && ergebnis === null && (
        <>
          <Karte titel="2. Spalten prüfen">
            <p className="text-sm text-neutral-600 dark:text-neutral-300">
              {vorschau.dateiname} · {vorschau.kodierung} · {vorschau.zeilen_gesamt} Zeilen
              {vorschau.zeilen_ohne_inventarnummer > 0 &&
                ` · ${String(vorschau.zeilen_ohne_inventarnummer)} ohne Inventarnummer`}
            </p>
            {vorschau.zeilen_mit_ungueltiger_nummer.length > 0 && (
              <p className="text-sm text-amber-800 dark:text-amber-300">
                Keine gültige Nummer:{" "}
                {vorschau.zeilen_mit_ungueltiger_nummer.slice(0, 10).join(", ")}
                {vorschau.zeilen_mit_ungueltiger_nummer.length > 10 && " …"}
              </p>
            )}
            <ul className="flex flex-col gap-2" aria-label="Spalten">
              {vorschau.spalten.map((s, i) => (
                <li key={`${s.ueberschrift}-${String(i)}`} className="flex flex-col gap-1">
                  <label className="flex flex-col gap-1">
                    <span className="text-sm font-semibold">
                      {s.ueberschrift.trim() === "" ? `Spalte ${String(i + 1)}` : s.ueberschrift}
                    </span>
                    <select
                      className="min-h-12 rounded-xl border-2 border-neutral-300 bg-white px-3 dark:border-neutral-600 dark:bg-neutral-800"
                      value={zuordnung[i] ?? "ignorieren"}
                      onChange={(e) => {
                        const neu = [...zuordnung];
                        neu[i] = e.target.value as ImportFeld;
                        setZuordnung(neu);
                      }}
                    >
                      {FELDER.map((f) => (
                        <option key={f} value={f}>
                          {FELD_TEXT[f]}
                        </option>
                      ))}
                    </select>
                  </label>
                  {s.beispiele.length > 0 && (
                    <span className="text-xs text-neutral-500 dark:text-neutral-400">
                      z. B. {s.beispiele.join(" · ")}
                    </span>
                  )}
                </li>
              ))}
            </ul>
            {zuordnungGeaendert && (
              <Knopf
                disabled={!online || laeuft}
                onClick={() =>
                  void ausfuehren(async () => {
                    neueVorschau(
                      await anfrage<ImportVorschau>(
                        "POST",
                        `/api/core/import/${vorschau.id}/vorschau`,
                        { zuordnung },
                      ),
                    );
                  })
                }
              >
                Übersicht neu berechnen
              </Knopf>
            )}
          </Karte>

          <Karte titel="3. Übersicht">
            <p className="font-semibold">
              {vorschau.anzahl.neu} neu · {vorschau.anzahl.geaendert} geändert ·{" "}
              {vorschau.anzahl.unveraendert} unverändert · {vorschau.anzahl.fehlend} nicht mehr in
              der Datei
            </p>

            <details open={vorschau.anzahl.neu > 0 && vorschau.anzahl.neu <= 20}>
              <summary className="min-h-12 cursor-pointer py-3 font-semibold">
                Neu ({vorschau.anzahl.neu})
              </summary>
              {vorschau.neu.length === 0 ? (
                <Leer>Nichts Neues.</Leer>
              ) : (
                <ul className="flex flex-col gap-1 text-sm" aria-label="Neu">
                  {vorschau.neu.map((a) => (
                    <li key={a.inventarnummer}>
                      <Nummer>{a.inventarnummer}</Nummer> {a.bezeichnung}
                    </li>
                  ))}
                  {vorschau.anzahl.neu > vorschau.neu.length && (
                    <li>… und {vorschau.anzahl.neu - vorschau.neu.length} weitere</li>
                  )}
                </ul>
              )}
            </details>

            <details open={vorschau.anzahl.geaendert > 0}>
              <summary className="min-h-12 cursor-pointer py-3 font-semibold">
                Geändert ({vorschau.anzahl.geaendert})
              </summary>
              <ul className="flex flex-col gap-2 text-sm" aria-label="Geändert">
                {vorschau.geaendert.map((g) => (
                  <li
                    key={g.artikel_id}
                    className="rounded-lg bg-neutral-100 p-2 dark:bg-neutral-700"
                  >
                    <span className="font-semibold">
                      <Nummer>{g.inventarnummer}</Nummer> {g.bezeichnung}
                    </span>
                    <ul>
                      {g.aenderungen.map((a) => (
                        <li key={a.feld}>
                          {feldName(a.feld)}:{" "}
                          <span className="line-through">{a.alt ?? "leer"}</span> →{" "}
                          <span className="font-semibold">{a.neu ?? "leer"}</span>
                        </li>
                      ))}
                    </ul>
                  </li>
                ))}
                {vorschau.anzahl.geaendert > vorschau.geaendert.length && (
                  <li>… und {vorschau.anzahl.geaendert - vorschau.geaendert.length} weitere</li>
                )}
              </ul>
            </details>

            <details open={vorschau.anzahl.fehlend > 0 && vorschau.anzahl.fehlend <= 50}>
              <summary className="min-h-12 cursor-pointer py-3 font-semibold">
                Nicht mehr in der Datei ({vorschau.anzahl.fehlend})
              </summary>
              {vorschau.fehlend.length === 0 ? (
                <Leer>Alles, was in der App steht, ist auch in der Datei.</Leer>
              ) : (
                <section aria-label="Nicht mehr in der Datei" className="flex flex-col gap-1">
                  <p className="text-sm text-neutral-600 dark:text-neutral-300">
                    Bleibt, wie es ist — außer es ist angekreuzt, dann wird es archiviert.
                  </p>
                  <Knopf
                    art="still"
                    onClick={() => {
                      setArchivieren(
                        archivieren.size === vorschau.fehlend.length
                          ? new Set()
                          : new Set(vorschau.fehlend.map((f) => f.artikel_id)),
                      );
                    }}
                  >
                    {archivieren.size === vorschau.fehlend.length
                      ? "Keins ankreuzen"
                      : "Alle ankreuzen"}
                  </Knopf>
                  <ul className="flex flex-col">
                    {vorschau.fehlend.map((f) => (
                      <li key={f.artikel_id}>
                        <label className="flex min-h-12 cursor-pointer items-center gap-3 border-b border-neutral-200 py-1 last:border-b-0 dark:border-neutral-700">
                          <input
                            type="checkbox"
                            className="size-6 shrink-0 accent-blue-700"
                            checked={archivieren.has(f.artikel_id)}
                            aria-label={`${f.bezeichnung} ${f.inventarnummer} archivieren`}
                            onChange={() => {
                              const neu = new Set(archivieren);
                              if (neu.has(f.artikel_id)) neu.delete(f.artikel_id);
                              else neu.add(f.artikel_id);
                              setArchivieren(neu);
                            }}
                          />
                          <span className="text-sm">
                            <Nummer>{f.inventarnummer}</Nummer> {f.bezeichnung}
                          </span>
                        </label>
                      </li>
                    ))}
                  </ul>
                </section>
              )}
            </details>
          </Karte>

          <Knopf
            art="haupt"
            disabled={!online || laeuft || zuordnungGeaendert}
            onClick={() =>
              void ausfuehren(async () => {
                const e = await anfrage<ImportErgebnis>(
                  "POST",
                  `/api/core/import/${vorschau.id}/uebernehmen`,
                  { zuordnung, archivieren: [...archivieren] },
                  120_000,
                );
                setErgebnis(e);
                setVorschau(null);
                void zwischenspeicher.invalidateQueries();
                void abgleichen({ stammdaten: true });
              })
            }
          >
            {`Übernehmen${archivieren.size > 0 ? ` und ${String(archivieren.size)} archivieren` : ""}`}
          </Knopf>
          {zuordnungGeaendert && (
            <p className="-mt-2 text-sm text-neutral-600 dark:text-neutral-300">
              Die Spalten sind geändert — erst die Übersicht neu berechnen.
            </p>
          )}
        </>
      )}

      <Karte titel="Letzte Läufe">
        {(laeufe.data?.laeufe ?? []).length === 0 ? (
          <Leer>Noch nichts eingelesen.</Leer>
        ) : (
          <ul className="flex flex-col gap-1 text-sm">
            {(laeufe.data?.laeufe ?? []).slice(0, 10).map((l) => (
              <li key={l.id}>
                {datum(l.hochgeladen_am)} · {l.dateiname} ·{" "}
                {l.ergebnis === null
                  ? "nicht übernommen"
                  : `${String(l.ergebnis.angelegt)} neu, ${String(l.ergebnis.aktualisiert)} geändert`}
              </li>
            ))}
          </ul>
        )}
      </Karte>
    </div>
  );
}
